import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tag, Calendar, Plus, ArrowRight, CheckCircle, Lightbulb, ChevronDown, ChevronRight } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface PostType {
  id: number;
  name: string;
  category: string;
}

interface ContentIdea {
  id: number;
  title: string;
  description?: string;
  post_type_name?: string;
  priority: string;
  status: string;
  estimated_effort: string;
  strategy_id?: number;
  created_at: string;
}

interface Strategy {
  id: number;
  name: string;
  description?: string;
  type: string;
  status: string;
  start_date: string;
  end_date: string;
  total_posts: number;
  published_posts: number;
  linkedIdeas?: ContentIdea[];
}

interface Props {
  clientId: number;
}

export default function ContentWorkflowTab({ clientId }: Props) {
  const [postTypes, setPostTypes] = useState<PostType[]>([]);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [selectedPostType, setSelectedPostType] = useState<PostType | null>(null);
  const [expandedStrategy, setExpandedStrategy] = useState<number | null>(null);
  const [ideas, setIdeas] = useState<ContentIdea[]>([]);
  const [allIdeas, setAllIdeas] = useState<ContentIdea[]>([]);
  const [loading, setLoading] = useState(true);
  const [ideaCounts, setIdeaCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    loadData();
  }, [clientId]);

  useEffect(() => {
    if (selectedPostType) {
      loadIdeasForPostType(selectedPostType.name);
    }
  }, [selectedPostType]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadPostTypes(), loadStrategies(), loadAllIdeas()]);
    } finally {
      setLoading(false);
    }
  };

  const loadPostTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/post-types`, {
        params: { client_id: clientId }
      });
      setPostTypes(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedPostType(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load post types:', error);
    }
  };

  const loadStrategies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/simple-plans`, {
        params: { client_id: clientId }
      });
      const strategiesData = response.data || [];

      // Load linked ideas for each strategy
      const strategiesWithIdeas = await Promise.all(
        strategiesData.map(async (strategy: Strategy) => {
          try {
            const ideasResponse = await axios.get(`${API_BASE_URL}/simple-plans/${strategy.id}/ideas`);
            return {
              ...strategy,
              linkedIdeas: ideasResponse.data.ideas || []
            };
          } catch (error) {
            return { ...strategy, linkedIdeas: [] };
          }
        })
      );

      setStrategies(strategiesWithIdeas);
    } catch (error) {
      console.error('Failed to load strategies:', error);
    }
  };

  const loadAllIdeas = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/ideas`, {
        params: { client_id: clientId }
      });
      const ideas = response.data.ideas || [];
      setAllIdeas(ideas);

      // Calculate idea counts per post type
      const counts: Record<string, number> = {};
      ideas.forEach((idea: ContentIdea) => {
        if (idea.post_type_name) {
          counts[idea.post_type_name] = (counts[idea.post_type_name] || 0) + 1;
        }
      });
      setIdeaCounts(counts);
    } catch (error) {
      console.error('Failed to load all ideas:', error);
    }
  };

  const loadIdeasForPostType = async (postTypeName: string) => {
    const filtered = allIdeas.filter((idea: ContentIdea) => idea.post_type_name === postTypeName);
    setIdeas(filtered);
  };

  const handleLinkIdeaToStrategy = async (ideaId: number, strategyId: number) => {
    try {
      await axios.put(`${API_BASE_URL}/ideas/${ideaId}`, {
        strategy_id: strategyId,
        converted_to_strategy_at: new Date().toISOString()
      });

      // Reload data
      await loadData();
      if (selectedPostType) {
        loadIdeasForPostType(selectedPostType.name);
      }
    } catch (error) {
      console.error('Failed to link idea to strategy:', error);
      alert('Failed to link idea to strategy');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      idea: 'bg-gray-100 text-gray-700',
      under_review: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      on_hold: 'bg-orange-100 text-orange-700',
      draft: 'bg-gray-100 text-gray-700',
      active: 'bg-green-100 text-green-700',
      completed: 'bg-blue-100 text-blue-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  const approvedIdeas = ideas.filter(idea => idea.status === 'approved' && !idea.strategy_id);

  return (
    <div className="flex h-[calc(100vh-300px)] gap-0">
      {/* Left Column: Post Types (20%) */}
      <div className="w-[20%] bg-gray-50 border-r border-gray-200 flex flex-col overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-white">
          <h3 className="text-sm font-semibold text-gray-900">Post Types</h3>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {postTypes.length > 0 ? (
            postTypes.map(pt => (
              <button
                key={pt.id}
                onClick={() => setSelectedPostType(pt)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1 ${
                  selectedPostType?.id === pt.id
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Tag className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{pt.name}</span>
                </div>
                {ideaCounts[pt.name] > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white rounded-full text-xs font-medium">
                    {ideaCounts[pt.name]}
                  </span>
                )}
              </button>
            ))
          ) : (
            <div className="text-center py-12 text-xs text-gray-500">No post types yet</div>
          )}
        </div>
      </div>

      {/* Middle Column: Content Ideas (50%) */}
      <div className="w-[50%] flex flex-col overflow-hidden bg-white border-r border-gray-200">
        {selectedPostType ? (
          <>
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{selectedPostType.name}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {ideas.length} idea{ideas.length !== 1 ? 's' : ''} • {approvedIdeas.length} approved
                  </p>
                </div>
                <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  New Idea
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {ideas.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                  <Lightbulb className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No ideas for {selectedPostType.name} yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ideas.map(idea => (
                    <div
                      key={idea.id}
                      className={`rounded-lg border p-4 hover:shadow-md transition ${
                        idea.status === 'approved' && !idea.strategy_id
                          ? 'bg-green-50 border-green-300'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-sm font-medium text-gray-900">{idea.title}</h3>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(idea.status)}`}>
                              {idea.status.replace('_', ' ')}
                            </span>
                            {idea.strategy_id && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                In Strategy
                              </span>
                            )}
                          </div>
                          {idea.description && (
                            <p className="text-sm text-gray-600 mb-2">{idea.description}</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <span className="font-medium">Priority: {idea.priority}</span>
                            <span>•</span>
                            <span>Effort: {idea.estimated_effort}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            Select a post type to view ideas
          </div>
        )}
      </div>

      {/* Right Column: Content Strategies (30%) */}
      <div className="w-[30%] flex flex-col overflow-hidden bg-gray-50">
        <div className="p-4 border-b border-gray-200 bg-white">
          <h3 className="text-sm font-semibold text-gray-900">Content Strategies</h3>
          <p className="text-xs text-gray-500 mt-1">{strategies.length} active strategies</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {strategies.length > 0 ? (
            <div className="space-y-3">
              {strategies.map(strategy => (
                <div key={strategy.id} className="bg-white rounded-lg border border-gray-200 shadow-sm">
                  {/* Strategy Header */}
                  <button
                    onClick={() => setExpandedStrategy(expandedStrategy === strategy.id ? null : strategy.id)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 transition"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {expandedStrategy === strategy.id ? (
                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <h4 className="text-sm font-medium text-gray-900 truncate">{strategy.name}</h4>
                        </div>
                        <div className="flex items-center gap-2 ml-6">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(strategy.status)}`}>
                            {strategy.status}
                          </span>
                          <span className="text-xs text-gray-500 capitalize">{strategy.type}</span>
                        </div>
                      </div>
                      {strategy.linkedIdeas && strategy.linkedIdeas.length > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                          {strategy.linkedIdeas.length}
                        </span>
                      )}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {expandedStrategy === strategy.id && (
                    <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                      {/* Linked Ideas */}
                      {strategy.linkedIdeas && strategy.linkedIdeas.length > 0 && (
                        <div className="mb-3">
                          <h5 className="text-xs font-semibold text-gray-700 mb-2">Linked Ideas ({strategy.linkedIdeas.length})</h5>
                          <div className="space-y-1">
                            {strategy.linkedIdeas.map(idea => (
                              <div key={idea.id} className="bg-white rounded px-3 py-2 text-xs border border-gray-200">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                                  <span className="text-gray-900 flex-1 truncate">{idea.title}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Add Approved Ideas Section */}
                      {approvedIdeas.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-700 mb-2">
                            Add Approved Ideas ({approvedIdeas.length} available)
                          </h5>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {approvedIdeas.map(idea => (
                              <button
                                key={idea.id}
                                onClick={() => handleLinkIdeaToStrategy(idea.id, strategy.id)}
                                className="w-full bg-green-50 hover:bg-green-100 rounded px-3 py-2 text-xs border border-green-200 text-left transition"
                              >
                                <div className="flex items-center gap-2">
                                  <ArrowRight className="w-3 h-3 text-green-600 flex-shrink-0" />
                                  <span className="text-gray-900 flex-1 truncate">{idea.title}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {approvedIdeas.length === 0 && (!strategy.linkedIdeas || strategy.linkedIdeas.length === 0) && (
                        <p className="text-xs text-gray-500 italic">No approved ideas available for this post type</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-600">No strategies yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}